import { ContactsView } from "@/components/contact/ContactsView";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { StrikeDiagonal } from "@/components/StrikeDiagonal";
import DaysCountTag from "@/components/tags/DaysCountTag";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatAddress, formatLocaleDate } from "@/utils/formatUtils";
import { useUpdateFilterSearchParams } from "@/utils/hooksUtils";
import { getTableSorting, sortableColumnProps } from "@/utils/tableUtils";
import { cssVariables, getMainContacts, isDefined, numberOrZero, toDate } from "@/utils/utils";
import { Card, Divider, Popover, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useDownloadClientsExport, useFilterClients } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { Permission } from "../../../../common/security/enums";
import { AgentTypeTagWithName } from "../../../agent/components/tags/AgentTypeTagWithName";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { useCurrentAgentAccount, useHasPermissions, useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { DEFAULT_FILTER_CLIENTS_QUERY_PARAMS } from "../../api";
import { ClientOrderByColumn, ClientPremiumType } from "../../enums";
import { CLIENT_ROUTE_PATHS } from "../../paths";
import { ClientFilterPageRequest, ClientList } from "../../types";
import { ClientTypeTagWithName } from "../tags/ClientTypeTagWithName";

interface Props {
  searchParams: ClientFilterPageRequest;
}

const ClientListTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const agentUserAccount = useCurrentAgentAccount();
  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);
  const hasClientAccountsPermission = useHasPermissions(Permission.CLIENT_ACCOUNTS);

  const updateSearchParams = useUpdateFilterSearchParams<ClientFilterPageRequest>(DEFAULT_FILTER_CLIENTS_QUERY_PARAMS);

  const { data, isFetching } = useFilterClients(searchParams);
  const downloadClientsExport = useDownloadClientsExport();

  const columns = useMemo(() => {
    const items: ColumnsType<ClientList> = [
      {
        key: "aggregatedName",
        title: t("client.attrs.aggregatedName"),
        width: 215,
        ...sortableColumnProps(ClientOrderByColumn.NAME, data),
        ellipsis: { showTitle: false },
        render: (_, record) => <ClientTypeTagWithName client={record} ellipsis showLink />,
      },
    ];

    if (isTopAgentPlus) {
      items.push({
        key: "premium",
        width: 40,
        align: "center",
        render: (_, record) => (
          <Tooltip title={t(`client.enums.premiumTypeLong.${record.premiumType}`)}>
            <span>
              {record.premiumType === ClientPremiumType.FULL && <AntIcon type="crown" color="yellow" />}
              {record.premiumType === ClientPremiumType.PARTIAL && <AntIcon type="build" color="orange" />}
              {record.premiumType === ClientPremiumType.NONE && (
                <StrikeDiagonal lineLengthPercentage={85} lineColor={cssVariables.colorGrey}>
                  <AntIcon type="dollar" color="grey" />
                </StrikeDiagonal>
              )}
            </span>
          </Tooltip>
        ),
      });
    }

    items.push(
      {
        key: "contacts",
        title: t("client.attrs.contacts"),
        width: 80,
        align: "center",
        render: (_, record) =>
          record.contacts.length > 0 && (
            <Popover
              title={t("client.helpers.mainContacts")}
              content={
                <div className="margin-top-tiny">
                  <ContactsView
                    contacts={getMainContacts(record.contacts)}
                    marketingConsentsAreApplicable={false}
                    size="small"
                  />
                </div>
              }
            >
              <span>
                <AntIcon type="contact" />
              </span>
            </Popover>
          ),
      },
      {
        key: "personalIdentificationNumberOrCompanyRegistrationNumber",
        title: (
          <>
            {t("client.attrs.personalIdentificationNumber")} / {t("client.attrs.companyRegistrationNumber")}
          </>
        ),
        width: 110,
        render: (_, record) => record.identifier,
      },
      {
        key: "address",
        title: t("client.attrs.address"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAddress(record.address)}</Ellipsis>,
      },
      {
        key: "lastContractDate",
        title: t("client.attrs.lastContractDate"),
        width: 105,
        ...sortableColumnProps(ClientOrderByColumn.LAST_CONTRACT_DATE, data),
        render: (_, record) => (record.lastContractDate ? formatLocaleDate(record.lastContractDate) : undefined),
      },
      {
        key: "contactClientDate",
        title: (
          <LabelWithTooltip
            tooltip={t("client.helpers.contactClientDateHint")}
            label={t("client.attrs.contactClientDate")}
          />
        ),
        width: 120,
        ...sortableColumnProps(ClientOrderByColumn.CONTACT_CLIENT_DATE, data),
        render: (_, record) => {
          const days = toDate(record.contactClientDate)?.startOf("day").diff(dayjs().startOf("day"), "days");

          return (
            <Tooltip title={formatLocaleDate(record.contactClientDate)}>
              <span>{isDefined(days) ? <DaysCountTag days={days} /> : undefined}</span>
            </Tooltip>
          );
        },
      },
      {
        key: "managedBy",
        title: t("client.attrs.managedBy"),
        width: 190,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <AgentTypeTagWithName
            agent={record.managedBy}
            ellipsis
            showLink={
              hasReadAgentPermission &&
              !!agentUserAccount?.agent.id &&
              record.managedBy.accessTreePath?.includes(agentUserAccount?.agent.id)
            }
          />
        ),
      },
    );

    if (hasClientAccountsPermission) {
      items.push({
        key: "hasMobileAppHint",
        align: "center",
        width: 40,
        render: (_, record) =>
          record.hasMobileApp ? (
            <Tooltip title={t("client.helpers.hasMobileAppHint")}>
              <span>
                <AntIcon type="mobile" color="green" />
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t("client.helpers.hasNoMobileAppHint")}>
              <span>
                <StrikeDiagonal lineLengthPercentage={90} lineColor={cssVariables.colorRed}>
                  <AntIcon type="mobile" color="red" />
                </StrikeDiagonal>
              </span>
            </Tooltip>
          ),
      });
    }

    items.push({
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(CLIENT_ROUTE_PATHS.detail.to, { id: record.id })}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    });

    return items;
  }, [data, isTopAgentPlus, hasClientAccountsPermission, hasReadAgentPermission, agentUserAccount?.agent.id]);

  return (
    <Card className={"card-box"}>
      <ComponentWithPermission permissions={[Permission.CLIENT_EXPORT]}>
        <div className={data?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
          <ActionTextIcon
            icon="download"
            color="blue"
            text={t("common.exportXlsx")}
            onClick={() => downloadClientsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
          />

          <Divider orientation="vertical" />

          <ActionTextIcon
            icon="download"
            color="green"
            text={t("common.exportCsv")}
            onClick={() => downloadClientsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
          />
        </div>
      </ComponentWithPermission>

      <Table<ClientList>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        showSorterTooltip={false}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={(pagination, __, sorter) => {
          const { orderBy, orderDirections } = getTableSorting<ClientOrderByColumn>(sorter);

          updateSearchParams(
            { orderBy, orderDirections, pageIndex: pagination.current ? pagination.current - 1 : undefined },
            true,
            false,
          );
        }}
      />
    </Card>
  );
};

export default ClientListTable;
